
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";
import { formatRound } from "cd-eft/utils/mathUtilities";
import { action, observable } from "mobx";
import { TerraceDesignToolsController } from "cd-eft/services/eftSetApi";
import stores from "cd-eft/models/Stores";
import { StorageCaculationResponseDto } from "cd-eft/_codegen_/openapi-generator/eft-set";

const terraceStore = stores.AppStore.terraceDesignStore;
export class TerraceStorageCalculationVM {
    config = {
        terraceDesignSection: {
            waterElevation: {
                label: 'Water Elevation (ft)',
                inputType: 'number',
                min: 0.01,
                errorCode: 'Common_10',
                className: 'waterElevation'
            },
            requiredStorage: {
                label: 'Required Storage (ac-in)',
                inputType: 'number',
                readonly: true,
                className: 'requiredStorage'
            },
        },
        modelOutputsSection: {
            waterElevation: {
                label: 'For Water Elevation',
                inputType: 'title',
            },
            totalCut: {
                label: 'Total Cut (cy)',
                inputType: 'text',
                readonly: true,
            },
            totalFill: {
                label: 'Total Fill (cy)',
                inputType: 'text',
                readonly: true,
            },
            cutFillBalance: {
                label: 'Cut/Fill Balance (cy)',
                inputType: 'text',
                readonly: true,
            },
            cutFillRatio: {
                label: 'Cut/Fill Ratio',
                inputType: 'text',
                readonly: true,
            },
            strippingVolume: {
                label: 'Stripping Volume (cy)',
                inputType: 'text',
                readonly: true,
            },
            floodArea: {
                label: 'Flood Area (ac)',
                inputType: 'text',
                readonly: true,
            },
            computedStorage: {
                label: 'Computed Storage (ac-in)',
                inputType: 'text',
                readonly: true,
            }
        }
    }
    onClickInformation = false;
    @observable inputForms = {
        terraceDesignSection: {
            waterElevation: 0.00,
            requiredStorage: 0.00,
        },
        modelOutputsSection: {
            waterElevation: '0',
            requiredStorage: '0',
            totalCut: '0.00',
            totalFill: '0.00',
            cutFillBalance: '0.00',
            cutFillRatio: '0.00',
            strippingVolume: '0.00',
            floodArea: '0.00',
            computedStorage: '0.00',
        }
    };

    @observable load: boolean = false;
    @observable manualDisableCalculate: boolean = false;
    @observable hasResults: boolean = false;

    @observable formsValidation = {
        terraceDesignSection: {
            waterElevation: true,
            requiredStorage: true
        }
    };

    @observable redHighlighting = {
        terraceDesignSection: {
            waterElevation: false,
            requiredStorage: false
        }
    };

    @observable alignElementID: number = stores.AppStore.terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId;
    @observable defaultWaterElevation: number = stores.AppStore.terraceDesignStore.terraceReport.waterElevation;
    @observable designReqStorage: number = stores.AppStore.terraceDesignStore.terraceReport.requiredStorage;
    @observable gradientMessageDisplayed: boolean = false;

    @observable modelOutputResultsErrors = {
        modelOutputSection: {
            waterElevation: false,
            requiredStorage: false,
            totalCut: false,
            totalFill: false,
            cutFillBalance: false,
            cutFillRatio: false,
            strippingVolume: false,
            floodArea: false,
            computedStorage: false,
        }
    };

    buttonsControl = [
        { title: 'Close', disabled: false, onClick: () => { }, className: 'usa-button--outline' },
    ];

    @action.bound handleInputsOnChange = (e, section) => {
        const { name, value } = e.target;
        this.inputForms[section][name] = value;
    }

    @action.bound handleInputsOnBlur = async (e, section, blockErrorCode = false) => {
        await terraceStore.handleInputsOnBlur(e, section, blockErrorCode, this.config, this.formsValidation, this.redHighlighting, this.handleInputsOnChange)
    }

    @action.bound setDefaultInputs = async () => {
        try {
            const reportValues = await TerraceDesignToolsController.getTerraceSummaryResultsUsingGET(terraceStore.terraceDesignObject.alignedElementDesignParametersId);
            if (reportValues && reportValues.data) {
                const reportValuesData = reportValues.data;
                terraceStore.terraceReport = reportValuesData;
                if (reportValuesData.errorResponse && reportValuesData.errorResponse.errorType &&
                    reportValuesData.errorResponse.errorCode && reportValuesData.errorResponse.errorMessage) {
                    MessageCenter.addNotification(reportValuesData.errorResponse.errorMessage, reportValuesData.errorResponse.errorType, reportValuesData.errorResponse.errorCode);
                }

                if (stores.AppStore.terraceDesignStore.terraceType !== 20) {
                    this.inputForms.terraceDesignSection.waterElevation = stores.AppStore.terraceDesignStore.terraceReport.waterElevation.toFixed(2);
                    this.inputForms.terraceDesignSection.requiredStorage = formatRound(stores.AppStore.terraceDesignStore.terraceReport.requiredStorage, 2);
                } else {
                    if (!this.gradientMessageDisplayed) {
                        this.gradientMessageDisplayed = true;
                        await MessageCenter.addNotificationFromCode('Terrace_39')
                    }
                }
            }
            if (this.inputForms.terraceDesignSection.waterElevation === 0 &&
                this.inputForms.terraceDesignSection.requiredStorage === 0) {
                this.manualDisableCalculate = true;
            }
        } catch (err) {
            console.error('error: getTerraceSummaryResultsUsingGET()', err)
        }
    }

    onClickInformationToggle = () => {
        this.onClickInformation = !this.onClickInformation
    }

    get notGradientType() {
        return (stores.AppStore.terraceDesignStore.terraceType === 20 || this.inputForms.terraceDesignSection.waterElevation === undefined || this.manualDisableCalculate);
    }

    @action.bound onClickCalculate = async () => {
        const elemID: number = stores.AppStore.terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId;
        const waterEv: number = this.inputForms.terraceDesignSection.waterElevation;
        this.load = true;
        this.manualDisableCalculate = true;
        this.hasResults = false;
        try {
            const response = await TerraceDesignToolsController.getstorageCalculationUsingGET(elemID, waterEv);
            if (response && response.data && response.data !== undefined) {
                if (response.data.errorResponse?.errorCode && response.data.errorResponse.errorMessage
                    && response.data.errorResponse.errorType) {
                    MessageCenter.clearMessages();
                    MessageCenter.addNotification(response.data.errorResponse.errorMessage, response.data.errorResponse.errorType, response.data.errorResponse.errorCode);
                    this.hasResults = false;
                }
                else {
                    const data = response.data;
                    this.setResultsData(data);
                    this.hasResults = true;
                }
            }
        } catch (e) {
            console.error('Unexpected Error:', e);
        }
        finally {
            this.manualDisableCalculate = false;
            this.load = false;
        }
    };
    private setResultsData(data: StorageCaculationResponseDto) {
        const commonRounding = 2;
        this.inputForms.modelOutputsSection.waterElevation = data.waterElevation !== undefined ? data.waterElevation.toFixed(commonRounding) : '';
        this.inputForms.modelOutputsSection.requiredStorage = data.requiredStorage !== undefined ? data.requiredStorage.toFixed(commonRounding) : '';
        this.inputForms.modelOutputsSection.totalCut = data.totalCut !== undefined ? data.totalCut.toFixed(commonRounding) : '';
        this.inputForms.modelOutputsSection.totalFill = data.totalFill !== undefined ? data.totalFill.toFixed(commonRounding) : '';
        this.inputForms.modelOutputsSection.cutFillRatio = data.cutFillRatio !== undefined ? data.cutFillRatio.toFixed(commonRounding) : '';
        this.inputForms.modelOutputsSection.cutFillBalance = data.cutFillBalance !== undefined ? data.cutFillBalance.toFixed(commonRounding) : '';
        this.inputForms.modelOutputsSection.strippingVolume = data.strippingVolume !== undefined ? data.strippingVolume.toFixed(commonRounding) : '';
        this.inputForms.modelOutputsSection.floodArea = data.floodArea !== undefined ? data.floodArea.toFixed(commonRounding) : '';
        this.inputForms.modelOutputsSection.computedStorage = data.computedStorage !== undefined ? data.computedStorage.toFixed(commonRounding) : '';
        this.config.modelOutputsSection.waterElevation.label = `For water elevation ${this.inputForms.terraceDesignSection.waterElevation} ft`;
    }

}